/**
 * Communities Store
 * Zustand store for managing communities state
 * Follows Clean Architecture - all API calls through servicesContainer
 */

import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import type {
  CommunityProfile,
  CommunitiesFilters,
  InsertCommunityRequest,
  UpdateCommunityRequest,
} from '../types/communities-types';

interface CommunitiesStore {
  // State
  communities: CommunityProfile[];
  totalCount: number; // Total number of communities (for pagination)
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  filters: CommunitiesFilters;
  pageSizeOptions: number[]; // Available page size options

  // Actions
  loadCommunities: () => Promise<void>;
  createCommunity: (data: InsertCommunityRequest) => Promise<number>;
  updateCommunity: (id: number, data: UpdateCommunityRequest) => Promise<void>;
  deleteCommunity: (id: number, hard?: boolean) => Promise<void>;
  refresh: () => Promise<void>;
  setFilters: (filters: Partial<CommunitiesFilters>) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  reset: () => void;
}

const initialState = {
  communities: [],
  totalCount: 0,
  isLoading: false,
  error: null,
  lastUpdated: null,
  filters: {
    includeExpired: false,
    actorId: null,
    page: 1,
    pageSize: 50,
  },
  pageSizeOptions: [25, 50, 100, 200],
};

export const useCommunitiesStore = create<CommunitiesStore>((set, get) => ({
  ...initialState,

  /**
   * Load communities with current filters
   * Reads filter state and passes to use case
   * Implements client-side pagination
   */
  loadCommunities: async () => {
    set({ isLoading: true, error: null });
    try {
      // ✅ CRITICAL: Lazy service access inside action
      const services = servicesContainer.getServices();
      const { filters } = get();

      const result = await services.getCommunitiesUseCase.execute(
        filters.includeExpired,
        filters.actorId
      );

      // Client-side filtering for inactive communities (safety check)
      // When includeExpired is false, filter out both deleted AND expired communities
      // When includeExpired is true, show all communities (backend already includes deleted ones)
      let filteredCommunities = result.communities;
      if (!filters.includeExpired) {
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Start of today

        filteredCommunities = result.communities.filter(community => {
          // Filter out deleted communities (status = "Deleted")
          if (community.profileStatus === 'Deleted') {
            return false;
          }

          // Check expiration date
          if (!community.expirationDate) return true; // No expiration date means not expired

          // Parse date string directly (YYYY-MM-DD format) to avoid timezone issues
          const dateStr = community.expirationDate.split('T')[0]; // Extract YYYY-MM-DD
          const [year, month, day] = dateStr.split('-').map(Number);
          const expiryDate = new Date(year, month - 1, day); // month is 0-indexed
          expiryDate.setHours(0, 0, 0, 0);

          return expiryDate >= now; // Keep only non-expired
        });
      }

      // Sort by profile name (alphabetically)
      const sortedCommunities = filteredCommunities.sort((a, b) =>
        a.profileName.localeCompare(b.profileName)
      );

      // Client-side pagination
      const totalCount = sortedCommunities.length;
      const startIndex = (filters.page - 1) * filters.pageSize;
      const endIndex = startIndex + filters.pageSize;
      const paginatedCommunities = sortedCommunities.slice(startIndex, endIndex);

      set({
        communities: paginatedCommunities,
        totalCount,
        isLoading: false,
        lastUpdated: new Date(),
      });
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load communities',
        isLoading: false,
      });
    }
  },

  /**
   * Create a new community
   * After creation, automatically reload the list
   * @returns profileId of the created community
   */
  createCommunity: async (data: InsertCommunityRequest): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.createCommunityUseCase.execute(data);

      if (!result.success) {
        throw new Error(result.message || 'Failed to create community');
      }

      // Reload list after creation
      await get().loadCommunities();

      // Return the created profileId
      return result.profileId;
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create community',
        isLoading: false,
      });
      throw error; // Re-throw so dialog can handle it
    }
  },

  /**
   * Update an existing community
   * After update, automatically reload the list
   */
  updateCommunity: async (id: number, data: UpdateCommunityRequest) => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.updateCommunityUseCase.execute(id, data);

      if (!result.success) {
        throw new Error(result.message || 'Failed to update community');
      }

      // Reload list after update
      await get().loadCommunities();
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update community',
        isLoading: false,
      });
      throw error; // Re-throw so dialog can handle it
    }
  },

  /**
   * Delete a community (soft or hard delete)
   * After deletion, automatically reload the list
   * @param id - Community profile ID
   * @param hard - If true, permanent deletion; if false (default), soft delete
   */
  deleteCommunity: async (id: number, hard: boolean = false) => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      await services.deleteCommunityUseCase.execute(id, hard);

      // Reload list after deletion
      await get().loadCommunities();
    } catch (error: unknown) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete community',
        isLoading: false,
      });
      throw error; // Re-throw so dialog can handle it
    }
  },

  /**
   * Refresh communities (alias for loadCommunities)
   * For semantic clarity in UI
   */
  refresh: async () => {
    await get().loadCommunities();
  },

  /**
   * Update filter state
   * Does NOT auto-reload - caller must call loadCommunities() after
   */
  setFilters: (newFilters: Partial<CommunitiesFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  /**
   * Set current page
   * Automatically reloads communities
   */
  setPage: async (page: number) => {
    set((state) => ({
      filters: { ...state.filters, page },
    }));
    await get().loadCommunities();
  },

  /**
   * Set page size
   * Resets to page 1 and automatically reloads communities
   */
  setPageSize: async (pageSize: number) => {
    set((state) => ({
      filters: { ...state.filters, pageSize, page: 1 }, // Reset to page 1 when changing page size
    }));
    await get().loadCommunities();
  },

  /**
   * Reset store to initial state
   */
  reset: () => set(initialState),
}));
